// @ts-check
// profileService.js
// Purpose: Load and persist the current user's profile (including timezone) using the
// centralized localStorage-backed cacheManager. Provides read/write helpers.
//
// Usage:
//   import { getUserProfile, updateUserProfile, getTimezone } from './profileService';
//   const profile = await getUserProfile(userId);
//   const tz = await getTimezone(userId); // cached fast path
//
// Notes:
// - Uses cacheManager for persistence; data is stored under cacheKey 'user_profile'.
// - We cache a single object (not an array). cacheManager supports any JSON via set/get.
// - If you need to force a refresh from Supabase, pass { forceRefresh: true }.

import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';
import { cacheManager } from '../cacheManager';
import { UserProfileSchema } from '../../lib/schemas/profile';

const CACHE_KEY = 'user_profile';

/**
 * Fetch the user's profile from Supabase and cache it locally.
 * @param {string} userId
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal }} [options]
 * @returns {Promise<Record<string, any> | null>}
 */
export async function getUserProfile(userId, { forceRefresh = false, signal } = {}) {
  if (!userId) {
    logger.warn('profileService.getUserProfile called without userId');
    return null;
  }

  // Try cache first unless forceRefresh
  if (!forceRefresh) {
    const cached = cacheManager.get(CACHE_KEY, userId);
    if (cached && typeof cached === 'object') {
      logger.debug('✅ profileService.getUserProfile: cache hit');
      return cached;
    }
  }

  // Fetch from Supabase
  logger.info('🔄 profileService.getUserProfile: fetching from Supabase');
  let query = supabase
    .from('user_profiles')
    .select(
      'user_id, first_name, last_name, timezone, walkthrough_state, life_areas, task_display_prefs, first_day_of_week, calendar_sync_enabled, calendar_privacy_mode, calendar_selected_ids, calendar_last_sync_at, calendar_colors, notification_quiet_hours',
    )
    .eq('user_id', userId)
    .single();
  // Some versions of Postgrest client expose .abortSignal; safely feature-detect
  if (signal && query) {
    const anyQuery = /** @type {any} */ (query);
    if (typeof anyQuery.abortSignal === 'function') {
      query = anyQuery.abortSignal(signal);
    }
  }

  const { data, error } = await query;
  if (error) {
    logger.error('❌ profileService.getUserProfile: Supabase error', error);
    // return cached as a fallback if available
    const fallback = cacheManager.get(CACHE_KEY, userId);
    return fallback && typeof fallback === 'object' ? fallback : null;
  }

  // Normalize nested JSONB fields for consistent structure
  if (data) {
    data.walkthrough_state = normalizeWalkthroughState(data.walkthrough_state);
    data.life_areas = normalizeLifeAreas(data.life_areas);
  }

  // Zod validation (non-blocking)
  const parsed = UserProfileSchema.safeParse(data);
  if (!parsed.success) {
    logger.warn('profileService.getUserProfile: Profile validation failed (non-blocking)', {
      userId,
      issues: parsed.error.issues?.slice(0, 3),
    });
    cacheManager.set(CACHE_KEY, data, userId);
    return data || null;
  }
  cacheManager.set(CACHE_KEY, parsed.data, userId);
  return parsed.data || null;
}

/**
 * Update the user's profile in Supabase and reflect it in cache.
 * @param {string} userId
 * @param {Record<string, any>} updates
 * @returns {Promise<Record<string, any> | null>}
 */
export async function updateUserProfile(userId, updates) {
  if (!userId) {
    logger.warn('profileService.updateUserProfile called without userId');
    return null;
  }
  logger.info('✏️ profileService.updateUserProfile: updating profile');
  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('user_id', userId)
    .select(
      'user_id, first_name, last_name, timezone, walkthrough_state, life_areas, task_display_prefs, first_day_of_week, calendar_sync_enabled, calendar_privacy_mode, calendar_selected_ids, calendar_last_sync_at, calendar_colors, notification_quiet_hours',
    )
    .single();

  if (error) {
    logger.error('❌ profileService.updateUserProfile: Supabase error', error);
    throw error;
  }

  // Normalize nested JSONB fields before caching
  if (data) {
    data.walkthrough_state = normalizeWalkthroughState(data.walkthrough_state);
    data.life_areas = normalizeLifeAreas(data.life_areas);
  }
  cacheManager.set(CACHE_KEY, data, userId);
  return data || null;
}

/**
 * Get the user's timezone with sensible fallbacks.
 * Reads from cache first, then Supabase, then auth metadata, then browser.
 * @param {string} userId
 * @returns {Promise<string>}
 */
export async function getTimezone(userId) {
  // 1) Cache
  const cached = cacheManager.get(CACHE_KEY, userId);
  if (cached?.timezone) return cached.timezone;

  // 2) Supabase profile
  const profile = await getUserProfile(userId).catch(() => null);
  if (profile?.timezone) return profile.timezone;

  // 3) Auth metadata fallback (if available globally)
  try {
    const authKeys = Object.keys(localStorage).filter((k) => k.includes('auth-token'));
    if (authKeys.length) {
      const key = authKeys[0];
      const raw = key ? localStorage.getItem(key) : null;
      if (raw) {
        const authData = JSON.parse(raw);
        const userMeta = authData?.user;
        const metaTz = userMeta?.user_metadata?.timezone || userMeta?.raw_user_meta_data?.timezone;
        if (metaTz) return metaTz;
      }
    }
  } catch {
    // noop: fall back to browser timezone below
  }

  // 4) Browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Normalize walkthrough_state to ensure expected structure
 * Tracks: onboarding_chat (chat completion) and primary-tour (UI tour)
 * @param {Record<string, any> | null | undefined} walkthroughState
 * @returns {Record<string, any>}
 */
export function normalizeWalkthroughState(walkthroughState) {
  const defaultState = {
    // Chat-based onboarding completion
    onboarding_chat: {
      completed: false,
      last_session_id: null,
      completed_at: null,
    },
    // UI tour state (object format with started_at for drop-off tracking)
    'primary-tour': {
      status: null, // 'in-progress', 'completed', 'dismissed', or null
      started_at: null,
      completed_at: null,
    },
  };

  if (!walkthroughState || typeof walkthroughState !== 'object') {
    return defaultState;
  }

  // Handle legacy string format for primary-tour (migrate to object)
  let primaryTour = walkthroughState['primary-tour'];
  if (typeof primaryTour === 'string') {
    // Migrate legacy string status to object format
    primaryTour = {
      status: primaryTour,
      started_at: null, // Unknown for legacy data
      completed_at: primaryTour === 'completed' || primaryTour === 'dismissed' ? null : null,
    };
  } else if (primaryTour && typeof primaryTour === 'object') {
    // Already object format, ensure all fields exist
    primaryTour = {
      status: primaryTour.status ?? defaultState['primary-tour'].status,
      started_at: primaryTour.started_at ?? defaultState['primary-tour'].started_at,
      completed_at: primaryTour.completed_at ?? defaultState['primary-tour'].completed_at,
    };
  } else {
    primaryTour = defaultState['primary-tour'];
  }

  // Merge with defaults to ensure all fields exist
  /** @type {Record<string, any>} */
  const result = {
    onboarding_chat: {
      completed:
        walkthroughState.onboarding_chat?.completed ?? defaultState.onboarding_chat.completed,
      last_session_id:
        walkthroughState.onboarding_chat?.last_session_id ??
        defaultState.onboarding_chat.last_session_id,
      completed_at:
        walkthroughState.onboarding_chat?.completed_at ?? defaultState.onboarding_chat.completed_at,
    },
    'primary-tour': primaryTour,
  };

  // Preserve any other walkthrough states (excluding legacy 'onboarding' object)
  Object.keys(walkthroughState).forEach((key) => {
    if (key !== 'onboarding' && key !== 'onboarding_chat' && key !== 'primary-tour') {
      result[key] = walkthroughState[key];
    }
  });

  return result;
}

/**
 * Normalize life_areas to ensure all life areas have default values
 * @param {Record<string, any> | null | undefined} lifeAreas
 * @returns {Record<string, any>}
 */
export function normalizeLifeAreas(lifeAreas) {
  const lifeAreaKeys = [
    // Life Areas
    'Health',
    'Mental & Emotional',
    'Partner & Love',
    'Family',
    'Friends',
    'Mission',
    'Experiences',
    'Spirit',
    'Finances',
    'Learning',
    // Daily Metrics
    'Daily_Clarity',
    'Daily_Energy',
    'Daily_Necessity',
    'Daily_Productivity',
    'Daily_Influence',
    'Daily_Courage',
    'Daily_Movement',
    'Daily_Mood',
    'Daily_Sleep',
    'Daily_Nutrition',
  ];

  const defaultArea = { current_value: 0, coverage: 0, date: new Date().toISOString() };
  /** @type {Record<string, any>} */
  const normalized = {};

  // Ensure all life areas exist with defaults including individual dates
  lifeAreaKeys.forEach((key) => {
    normalized[key] = {
      current_value: lifeAreas?.[key]?.current_value ?? defaultArea.current_value,
      coverage: lifeAreas?.[key]?.coverage ?? defaultArea.coverage,
      date: lifeAreas?.[key]?.date || new Date().toISOString(),
    };
  });

  // Preserve any additional keys (but not top-level date if it exists from old structure)
  if (lifeAreas && typeof lifeAreas === 'object') {
    Object.keys(lifeAreas).forEach((key) => {
      if (key !== 'date' && !lifeAreaKeys.includes(key)) {
        normalized[key] = lifeAreas[key];
      }
    });
  }

  return normalized;
}

/**
 * Get the walkthrough state for a user from their profile
 * @param {string} userId
 * @param {{ forceRefresh?: boolean, normalize?: boolean }} [options]
 * @returns {Promise<Record<string, string> | null>}
 */
export async function getWalkthroughState(userId, { forceRefresh = false, normalize = true } = {}) {
  if (!userId) {
    logger.warn('profileService.getWalkthroughState called without userId');
    return null;
  }

  const profile = await getUserProfile(userId, { forceRefresh });
  if (!profile) {
    return null;
  }

  const rawState = profile.walkthrough_state || {};

  // By default, normalize to ensure all fields exist with proper defaults
  return normalize ? normalizeWalkthroughState(rawState) : rawState;
}

/**
 * Update walkthrough status for a specific walkthrough ID
 * @param {string} userId
 * @param {string} walkthroughId
 * @param {'completed' | 'dismissed' | 'in-progress'} status
 * @returns {Promise<void>}
 */
export async function markWalkthroughStatus(userId, walkthroughId, status) {
  if (!userId || !walkthroughId || !status) {
    logger.warn('profileService.markWalkthroughStatus called with invalid params', {
      userId,
      walkthroughId,
      status,
    });
    return;
  }

  try {
    // Get current walkthrough state
    const currentState = await getWalkthroughState(userId);
    const updatedState = {
      ...(currentState || {}),
      [walkthroughId]: status,
    };

    // Update profile with new walkthrough state
    await updateUserProfile(userId, {
      walkthrough_state: updatedState,
    });

    logger.info('✅ profileService.markWalkthroughStatus: updated', {
      walkthroughId,
      status,
    });
  } catch (error) {
    logger.error('❌ profileService.markWalkthroughStatus: failed', error);
    throw error;
  }
}

/**
 * Get the user's task display preferences from their profile.
 * Returns the task_display_prefs JSONB or an empty object.
 * @param {string} userId
 * @returns {Promise<Record<string, any>>}
 */
export async function getTaskDisplayPrefs(userId) {
  if (!userId) return {};
  try {
    const profile = await getUserProfile(userId);
    const prefs = profile?.task_display_prefs;
    return prefs && typeof prefs === 'object' ? prefs : {};
  } catch (err) {
    logger.warn('profileService.getTaskDisplayPrefs: failed, returning empty', err);
    return {};
  }
}

/**
 * Update the user's task display preferences in Supabase.
 * Merges the provided prefs with existing ones.
 * @param {string} userId
 * @param {Record<string, any>} prefs
 * @returns {Promise<Record<string, any> | null>}
 */
export async function updateTaskDisplayPrefs(userId, prefs) {
  if (!userId || !prefs || typeof prefs !== 'object') return null;
  try {
    const current = await getTaskDisplayPrefs(userId);
    const merged = { ...current, ...prefs };
    return await updateUserProfile(userId, { task_display_prefs: merged });
  } catch (err) {
    logger.warn('profileService.updateTaskDisplayPrefs: failed', err);
    return null;
  }
}

export default {
  getUserProfile,
  updateUserProfile,
  getTimezone,
  normalizeWalkthroughState,
  normalizeLifeAreas,
  getWalkthroughState,
  markWalkthroughStatus,
  getTaskDisplayPrefs,
  updateTaskDisplayPrefs,
};
